/**
 * The browser half of the same contract: `console.*`, event name first.
 *
 *   console.info("discount.rule.copied", { ruleId });
 *   console.error("app.page.load.failed", error, { path });
 *
 * Unlike the server, the browser console still prints normally — devtools output is the whole
 * point of a browser console and turning it into JSON would make development worse. What this
 * adds is a second destination: warnings and errors are forwarded to New Relic Browser with the
 * event name, the shop and the path, so a failure a merchant hit is visible without someone
 * having had devtools open at the time.
 *
 * PostHog deliberately receives none of this. It is product/funnel analytics; technical errors
 * there are noise that makes the funnels less trustworthy.
 */

type SafeScalar = string | number | boolean | null;

const RESERVED_FIELDS = new Set([
  "shop",
  "path",
  "pageRequestId",
  "event",
  "level",
  "time",
  "error",
]);

const CONSOLE_KEY = "__aiodClientConsole";

type ConsoleMethod = "log" | "info" | "warn" | "error" | "debug";

const METHODS: ConsoleMethod[] = ["log", "info", "warn", "error", "debug"];

let configuredShop: string | null = null;
let configuredPageRequestId: string | null = null;

const root = globalThis as typeof globalThis & {
  [CONSOLE_KEY]?: Pick<Console, ConsoleMethod>;
};

/**
 * Names the shop and the page load. Called from the root loader's data, so it runs after the
 * capture is already installed.
 *
 * `pageRequestId` is the id of the request that served *this document*, not of whichever fetch
 * was in flight when something failed. That is the useful join: it connects a browser error to
 * the server-side records for the page load the merchant was looking at.
 */
export function configureClientLogger(
  shop: string | null,
  pageRequestId: string | null = null,
): void {
  configuredShop = shop;
  configuredPageRequestId = pageRequestId;
}

/** Same grammar as the server, so one event name means one thing wherever it is emitted. */
const EVENT_NAME =
  /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*\.[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

export function isEventName(value: unknown): value is string {
  return (
    typeof value === "string" && value.length <= 120 && EVENT_NAME.test(value)
  );
}

function scalar(value: unknown): SafeScalar | undefined {
  if (
    value === null ||
    ["string", "number", "boolean"].includes(typeof value)
  ) {
    return value as SafeScalar;
  }
  try {
    const json = JSON.stringify(value);
    return json && json.length <= 1_000 ? json : undefined;
  } catch {
    return undefined;
  }
}

function attributes(event: string, data?: Record<string, unknown>) {
  const result: Record<string, SafeScalar> = {
    event,
    shop: configuredShop,
    pageRequestId: configuredPageRequestId,
    path:
      typeof window !== "undefined"
        ? (window.location?.pathname ?? null)
        : null,
  };
  for (const [key, value] of Object.entries(data ?? {})) {
    if (RESERVED_FIELDS.has(key)) continue;
    const safeValue = scalar(value);
    if (safeValue !== undefined) result[key] = safeValue;
  }
  return result;
}

function parse(method: ConsoleMethod, args: unknown[]) {
  const error = args.find((argument) => argument instanceof Error);
  if (isEventName(args[0])) {
    const rest = args.slice(1).filter((argument) => argument !== error);
    const data = rest.find(
      (argument) =>
        argument !== null &&
        typeof argument === "object" &&
        !Array.isArray(argument),
    ) as Record<string, unknown> | undefined;
    const message = rest.find((argument) => typeof argument === "string") as
      | string
      | undefined;
    return { event: args[0], data, message, error };
  }
  const message = args.find((argument) => typeof argument === "string") as
    | string
    | undefined;
  return { event: `console.${method}`, data: undefined, message, error };
}

/**
 * React's hydration-failure codes: #418 (structure differed), #419/#421/#422 (Suspense boundary),
 * #423 (whole root fell back to client rendering), #425 (text differed). The development build
 * spells them out instead.
 */
const REACT_HYDRATION_ERROR =
  /Minified React error #(418|419|421|422|423|425)\b|Hydration failed|There was an error while hydrating|Text content does not match server-rendered HTML/;

/**
 * React itself logs every error an error boundary caught, as a bare `console.error(error)` — in a
 * hydration attempt it goes on to discard and recover from, too. Forwarded, that copy put each
 * recovered page load in the error inbox as a `console.error`, which is exactly the false alarm
 * `app.render.recovered` exists to replace. It still prints to devtools. A hydration failure React
 * could *not* recover from still reaches the inbox, as the root ErrorBoundary's
 * `app.page.load.failed`.
 */
function isReactsOwnHydrationLog(event: string, error: unknown): boolean {
  return (
    event === "console.error" &&
    error instanceof Error &&
    REACT_HYDRATION_ERROR.test(error.message)
  );
}

/**
 * Wraps the browser console so warnings and errors also reach New Relic. The original output is
 * always produced first, so nothing a developer expects to see in devtools disappears — and it
 * is produced even if the telemetry throws.
 */
export function installClientConsoleCapture(): void {
  if (typeof window === "undefined" || root[CONSOLE_KEY]) return;

  const originals = {} as Pick<Console, ConsoleMethod>;
  for (const method of METHODS)
    originals[method] = console[method].bind(console);
  root[CONSOLE_KEY] = originals;

  for (const method of METHODS) {
    console[method] = (...args: unknown[]) => {
      originals[method](...(args as []));
      if (method !== "warn" && method !== "error") return;
      try {
        const { event, data, message, error } = parse(method, args);
        if (isReactsOwnHydrationLog(event, error)) return;
        window.newrelic?.log?.(message ?? event, {
          level: method,
          customAttributes: attributes(event, data),
        });
        if (method === "error") {
          window.newrelic?.noticeError?.(
            error instanceof Error ? error : new Error(message ?? event),
            attributes(event, data),
          );
        }
      } catch {
        // Browser telemetry must never affect the application.
      }
    };
  }
}

type RecoveredBatch = {
  first: unknown;
  codes: Map<string, number>;
};

let recoveredBatch: RecoveredBatch | null = null;

function reactErrorCode(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.match(/React error #(\d+)/)?.[1] ?? "other";
}

/**
 * `hydrateRoot`'s `onRecoverableError`: React hit an error it recovered from on its own — nearly
 * always a hydration mismatch (#418/#423/#425), after which it re-renders the page on the client —
 * and the merchant got a working page.
 *
 * A warning, not an error, and under its own name, so it stays out of the error inbox and the
 * alerting that `app.page.load.failed` feeds. The React message goes in `errorMessage` because
 * forwarded warnings carry only scalar fields, and the code in it (#418) is what says which kind of
 * mismatch it was. Frequent recoveries still merit a look — a mismatch on every load costs the
 * whole server render — but they are not broken pages.
 *
 * One recovery is reported as one record. React calls this once per mismatched element — a single
 * injected node produced 28 calls in a real browser — all synchronously, from the commit that
 * finishes the recovery; they are collected and sent together, with `recoveredErrors` and the
 * `errorCodes` seen ("418x27 423x1").
 */
export function reportRecoverableRenderError(error: unknown): void {
  if (recoveredBatch === null) {
    recoveredBatch = { first: error, codes: new Map() };
    queueMicrotask(flushRecoveredBatch);
  }
  const code = reactErrorCode(error);
  recoveredBatch.codes.set(code, (recoveredBatch.codes.get(code) ?? 0) + 1);
}

function flushRecoveredBatch(): void {
  const batch = recoveredBatch;
  recoveredBatch = null;
  if (batch === null) return;
  const total = [...batch.codes.values()].reduce((sum, count) => sum + count, 0);
  console.warn(
    "app.render.recovered",
    batch.first,
    {
      errorMessage:
        batch.first instanceof Error ? batch.first.message : String(batch.first),
      recoveredErrors: total,
      errorCodes: [...batch.codes].map(([code, count]) => `${code}x${count}`).join(" "),
    },
    "React recovered from a render error by re-rendering on the client",
  );
}

export function restoreClientConsole(): void {
  const originals = root[CONSOLE_KEY];
  if (!originals) return;
  for (const method of METHODS) console[method] = originals[method];
  delete root[CONSOLE_KEY];
}

const NAVIGATION_KEY = "__aiodNavigationId";

const navRoot = globalThis as typeof globalThis & { [NAVIGATION_KEY]?: string };

/**
 * The id shared by every request of one page load.
 *
 * `v3_singleFetch` is off, so a client navigation fetches each route's data in its own request.
 * Each correctly gets its own `requestId`; this is what lets someone ask for the page load they
 * are all part of. Set from React Router's history key, which changes once per navigation.
 */
export function setNavigationId(id: string | null): void {
  if (id) navRoot[NAVIGATION_KEY] = id;
}

export function getNavigationId(): string | undefined {
  return navRoot[NAVIGATION_KEY];
}
